import React, { useEffect, useState, useCallback } from "react";
import { FaPlus, FaUsers, FaTrash } from "react-icons/fa";
import { useAuth } from "../../context/Authcontext/AuthContext";
import {
  addStaffMember,
  DeleteStaffMember,
  updateStaffMember,
  listenToBranchStaff,
} from "./StaffFucntions";
import StaffCard from "./StaffCard";
import StaffForm from "./StaffForm";

const BranchStaffModal = ({ locationId, onClose }) => {
  const { currentUser } = useAuth();
  
  const [state, setState] = useState({
    showPopup: false,
    staff: [],
    isEditing: false,
    editingId: null,
    isLoading: true,
    pendingDelete: null,
  });

  const [formData, setFormData] = useState({
    firstName: "", lastName: "", email: "", mobileNo: "", password: "",
    selectedRole: "", address: "", city: "", state: "", pincode: "", dateOfBirth: "", pinCode: ""
  });

  const [deletingStaffId, setDeletingStaffId] = useState(null);

  const updateState = useCallback((updates) => setState((prev) => ({ ...prev, ...updates })), []);

  const clearFormData = useCallback(() => {
    setFormData({
      firstName: "", lastName: "", email: "", mobileNo: "", password: "",
      selectedRole: "", address: "", city: "", state: "", pincode: "", dateOfBirth: "", pinCode: ""
    });
    updateState({ isEditing: false, editingId: null });
  }, [updateState]);

  useEffect(() => {
    if (!currentUser?.uid || !locationId) return;

    const unsubscribe = listenToBranchStaff(
      currentUser.uid,
      locationId,
      (updatedStaff) => {
        updateState({ staff: updatedStaff, isLoading: false });
      }
    );

    return () => unsubscribe();
  }, [currentUser, locationId, updateState]);

  const hasManager = state.staff.some(member => member.role === "Manager");

  const handleEditStaff = (id) => {
    const staff = state.staff.find((member) => member.id === id);
    if (staff) {
      const [address, city, stateAndPin] = staff.address?.split(", ") || [];
      const [st, pin] = stateAndPin?.split(" - ") || [];
      setFormData({
        firstName: staff.firstName || "",
        lastName: staff.lastName || "",
        email: staff.email || "",
        mobileNo: staff.mobileNo || "",
        password: staff.password || "",
        selectedRole: staff.role || "",
        address: address || "",
        city: city || "",
        state: st || "",
        pincode: pin || "",
        dateOfBirth: staff.dateOfBirth || "",
        pinCode: staff.pinCode || "",
      });
      updateState({ showPopup: true, isEditing: true, editingId: id });
    }
  };

  const handleDeleteStaff = (id, role) => {
    updateState({ pendingDelete: { id, role } });
  };

  const executeDeleteStaff = async () => {
    if (!state.pendingDelete) return;
    const { id } = state.pendingDelete;
    
    setDeletingStaffId(id);
    updateState({ pendingDelete: null });
    try {
      await DeleteStaffMember(id, locationId);
      updateState({ staff: state.staff.filter((member) => member.id !== id) });
    } catch (err) {
      console.error("Failed to delete staff:", err);
      alert("Failed to delete: " + err.message);
    } finally {
      setDeletingStaffId(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-40 p-4 backdrop-blur-sm">
      <div className="bg-gray-50 flex flex-col rounded-2xl w-full max-w-6xl h-[90vh] shadow-2xl relative animate-in fade-in zoom-in duration-200">
        
        {/* Header */}
        <div className="bg-white px-8 py-5 border-b border-gray-100 flex justify-between items-center shrink-0 rounded-t-2xl">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Branch Staff</h2>
            <p className="text-gray-500 text-sm mt-1">Manage team members directly assigned to this location</p>
          </div>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-red-500 text-3xl transition-colors w-10 h-10 flex items-center justify-center rounded-full hover:bg-red-50"
          >
            &times;
          </button>
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto p-8 popup-scrollbar">
            <div className="flex justify-between items-center mb-8">
              <div className="flex items-center space-x-3 bg-white rounded-xl px-5 py-3 shadow-sm border border-gray-100">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <FaUsers className="text-blue-500 text-xl" />
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Total Team</span>
                  <span className="font-bold text-gray-900 text-lg leading-tight">{state.staff.length}</span>
                </div>
              </div>
            </div>

          {/* Manager Section */}
          <div className="mb-10">
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center border-b border-gray-200 pb-2">
              <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-lg text-sm mr-3 font-semibold uppercase tracking-wider">Manager</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {state.isLoading ? (
                <div className="animate-pulse bg-gray-200 h-32 rounded-xl"></div>
              ) : hasManager ? (
                state.staff
                  .filter((m) => m.role === "Manager")
                  .map((member) => (
                    <StaffCard
                      key={member.id}
                      member={member}
                      onEdit={handleEditStaff}
                      onDelete={() => handleDeleteStaff(member.id, member.role)}
                      isDeleting={deletingStaffId === member.id}
                    />
                  ))
              ) : (
                <button
                  onClick={() => {
                    clearFormData();
                    updateState({ showPopup: true });
                    setFormData((prev) => ({ ...prev, selectedRole: "Manager" }));
                  }}
                  className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-yellow-300 rounded-2xl bg-yellow-50 hover:bg-yellow-100 transition-colors text-yellow-700 min-h-[150px]"
                >
                  <FaPlus className="text-2xl mb-2" />
                  <span className="font-semibold text-lg">Add Manager</span>
                  <p className="text-sm mt-1 text-yellow-600 text-center">
                    Assign a leader for this branch
                  </p>
                </button>
              )}
            </div>
          </div>

          {/* Other Staff Section */}
          <div>
            <div className="flex justify-between items-center mb-4 border-b border-gray-200 pb-2">
              <h3 className="text-lg font-bold text-gray-800 flex items-center">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-lg text-sm mr-3 font-semibold uppercase tracking-wider">Other Staff</span>
              </h3>
              <button
                onClick={() => {
                  clearFormData();
                  updateState({ showPopup: true });
                  setFormData((prev) => ({ ...prev, selectedRole: "Staff Member" }));
                }}
                className="flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-all shadow-md text-sm font-medium"
              >
                <FaPlus />
                <span>Add Staff Member</span>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
              {state.staff.filter((m) => m.role !== "Manager").length ? (
                state.staff
                  .filter((m) => m.role !== "Manager")
                  .map((member) => (
                    <StaffCard
                      key={member.id}
                      member={member}
                      onEdit={handleEditStaff}
                      onDelete={() => handleDeleteStaff(member.id, member.role)}
                      isDeleting={deletingStaffId === member.id}
                    />
                  ))
              ) : (
                <div className="col-span-full border border-dashed border-gray-200 rounded-xl p-10 text-center bg-gray-50 flex flex-col items-center justify-center h-[200px]">
                  <FaUsers className="text-4xl text-gray-300 mb-3" />
                  <p className="text-gray-500 font-medium text-lg">
                    No additional staff members found.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Nested Add/Edit Staff Modal */}
        {state.showPopup && (
          <div className="absolute inset-0 z-50">
             <StaffForm
                state={state}
                formData={formData}
                setFormData={setFormData}
                updateState={updateState}
                clearFormData={clearFormData}
                locationId={locationId}
                addStaffMember={addStaffMember}
                updateStaffMember={updateStaffMember}
                currentUser={currentUser}
                hasManager={hasManager}
             />
           </div>
        )}

        {/* Delete Confirmation Modal */}
        {state.pendingDelete && (
          <div className="absolute inset-0 z-[60] bg-black bg-opacity-60 flex items-center justify-center p-4 backdrop-blur-sm rounded-2xl">
            <div className="bg-white rounded-2xl max-w-md w-full p-8 shadow-2xl relative transform transition-all">
              <div className="flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mx-auto mb-5 shadow-sm">
                <FaTrash className="text-red-500 text-2xl" />
              </div>
              <h2 className="text-2xl font-bold text-center text-gray-900 mb-3">
                {state.pendingDelete.role === "Manager" ? "Delete Branch Manager?" : "Delete Staff Member?"}
              </h2>
              <p className="text-center text-gray-500 mb-8 font-medium leading-relaxed">
                {state.pendingDelete.role === "Manager" 
                  ? "Are you sure you want to completely delete the Branch Manager? Their Firebase Authentication login credentials will also be permanently revoked."
                  : "Are you sure you want to delete this Staff Member?"}
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => updateState({ pendingDelete: null })}
                  className="flex-1 py-3 text-gray-700 font-semibold bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors focus:outline-none"
                >
                  Cancel
                </button>
                <button
                  onClick={executeDeleteStaff}
                  className="flex-1 py-3 text-white font-semibold bg-red-600 hover:bg-red-700 rounded-xl shadow-md transition-colors focus:outline-none"
                >
                  Confirm Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BranchStaffModal;
